import type { Canvas, CanvasLayer } from '../types';
import type { CanvasEntity } from '../entityTypes';

/** Captures the before/after state for each changed top-level key of CanvasStoreContext. */
export interface ContextDelta {
  canvas?: { before: Canvas; after: Canvas };
  activeLayerId?: { before: string; after: string };
  layers?: { before: CanvasLayer[]; after: CanvasLayer[] };
  nodes?: { before: Record<string, CanvasEntity>; after: Record<string, CanvasEntity> };
  connectors?: { before: Record<string, CanvasEntity>; after: Record<string, CanvasEntity> };
  groups?: { before: Record<string, CanvasEntity>; after: Record<string, CanvasEntity> };
  nodesVersion?: { before: number; after: number };
  connectorsVersion?: { before: number; after: number };
  groupsVersion?: { before: number; after: number };
}

export interface ExecutedCommand {
  command: CanvasCommand;
  delta: ContextDelta;
}

export interface Transaction {
  label: string;
  commands: ExecutedCommand[];
  timestamp: number;
}

/** Undo policy for a command type. */
export type UndoPolicy = 'undoable' | 'non-undoable' | 'batch-drag';

// Forward reference — the actual union is defined in registry.ts
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type CanvasCommand = import('./registry').CanvasCommand;

/** Events that should NOT trigger an undo checkpoint. */
const NON_UNDOABLE_TYPES = new Set([
  'layer.setActive',
  'entity.updateSize',
]);

/** Events that happen during drag — checkpointed via batch, not individually. */
const DRAG_TYPES = new Set([
  'entity.batchMove',
  'group.batchMoveCollapsed',
  'entity.resize',
]);

export function getUndoPolicy(commandType: string): UndoPolicy {
  if (NON_UNDOABLE_TYPES.has(commandType)) return 'non-undoable';
  if (DRAG_TYPES.has(commandType)) return 'batch-drag';
  return 'undoable';
}
